/**
 * Cache Invalidation Error Boundary
 * 
 * Enhanced error boundary that provides intelligent error handling during 
 * cache invalidation processes. Includes retry mechanisms, graceful degradation,
 * and user-friendly error recovery options.
 * 
 * Phase 3 implementation - robust error handling for cache operations
 */

import React, { Component, ReactNode } from 'react';
import { QueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/atoms/button';
import { Spinner } from '@/components/ui/atoms/spinner';
import { 
  invalidateBrandingCache,
  type BrandingInvalidationResult
} from '@/lib/services/brandingCacheIntegration';
import { 
  orchestrateCacheInvalidation,
  type CacheInvalidationResult
} from '@/lib/services/cacheInvalidationOrchestrator';
import { manualReset } from '@/lib/services/atomicReset';

interface Props {
  children: ReactNode;
  queryClient?: QueryClient;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  fallback?: ReactNode;
  enableRetry?: boolean;
  enableCacheReset?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  retryCount: number;
  isRetrying: boolean;
  cacheResetCount: number;
  isResetting: boolean;
  lastResetResult: CacheInvalidationResult | null;
}

// Development environment detection
const isDevelopment = process.env.NODE_ENV === 'development';

// Maximum retry attempts
const MAX_RETRIES = 3;
const MAX_CACHE_RESETS = 2;

/**
 * Enhanced error boundary with cache invalidation recovery capabilities
 */
export class CacheInvalidationErrorBoundary extends Component<Props, State> {
  private retryTimeout: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isRetrying: false,
      cacheResetCount: 0,
      isResetting: false,
      lastResetResult: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log error with enhanced context for cache-related issues
    const isCacheError = this.isCacheRelatedError(error);
    const logLevel = isCacheError ? 'warn' : 'error';
    
    console[logLevel]('CacheErrorBoundary: Error caught', {
      error: error.message,
      stack: error.stack,
      errorInfo,
      isCacheError,
      retryCount: this.state.retryCount,
      cacheResetCount: this.state.cacheResetCount,
    });

    // Auto-retry for cache-related errors in development
    if (isDevelopment && isCacheError && this.state.retryCount < MAX_RETRIES) {
      this.scheduleRetry(2000); // 2 second delay
    }
  }

  componentWillUnmount() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  /**
   * Determine if error is likely cache-related
   */
  private isCacheRelatedError(error: Error): boolean {
    const errorMessage = error.message.toLowerCase();
    const cacheKeywords = [
      'localstorage',
      'cache',
      'storage',
      'quota',
      'branding',
      'query',
      'fetch',
      'network',
      'timeout'
    ];

    return cacheKeywords.some(keyword => errorMessage.includes(keyword));
  }

  /**
   * Schedule retry with exponential backoff
   */
  private scheduleRetry(baseDelay: number = 1000): void {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }

    const delay = baseDelay * Math.pow(2, this.state.retryCount);
    
    this.setState({ isRetrying: true });

    this.retryTimeout = setTimeout(() => {
      this.handleRetry();
    }, delay);
  }

  /**
   * Handle retry attempt
   */
  private handleRetry = (): void => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      isRetrying: false,
      retryCount: prevState.retryCount + 1,
    }));

    if (isDevelopment) {
      console.log('CacheErrorBoundary: Retrying after error', {
        attempt: this.state.retryCount + 1,
      });
    }
  };

  /**
   * Handle cache reset attempt
   */
  private handleCacheReset = async (): Promise<void> => {
    if (!this.props.queryClient) {
      console.warn('CacheErrorBoundary: Cannot reset cache - no QueryClient provided');
      return;
    }

    this.setState({ isResetting: true });

    try {
      // Orchestrate full cache invalidation
      const result = await orchestrateCacheInvalidation(
        this.props.queryClient,
        'Error boundary cache reset'
      );

      // Also invalidate branding cache specifically
      const brandingResult = await invalidateBrandingCache(
        this.props.queryClient,
        undefined,
        'Error boundary branding reset'
      );

      this.setState(prevState => ({
        hasError: false,
        error: null,
        errorInfo: null,
        isResetting: false,
        cacheResetCount: prevState.cacheResetCount + 1,
        lastResetResult: result,
      }));

      if (isDevelopment) {
        console.log('CacheErrorBoundary: Cache reset completed', {
          cacheResult: result,
          brandingResult,
        });
      }
    } catch (resetError) {
      console.error('CacheErrorBoundary: Cache reset failed:', resetError);
      this.setState({ isResetting: false });
    }
  };

  /**
   * Handle manual atomic reset (development only)
   */
  private handleAtomicReset = async (): Promise<void> => {
    if (!isDevelopment) return;

    this.setState({ isResetting: true });

    try {
      await manualReset('Error boundary atomic reset');
      
      // Clear the error state after atomic reset
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        isResetting: false,
        retryCount: 0,
        cacheResetCount: 0,
      });

      if (isDevelopment) {
        console.log('CacheErrorBoundary: Atomic reset completed');
      }
    } catch (resetError) {
      console.error('CacheErrorBoundary: Atomic reset failed:', resetError);
      this.setState({ isResetting: false });
    }
  };

  /**
   * Reset error boundary state manually
   */
  private handleManualReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isRetrying: false,
    });
  };

  render(): ReactNode {
    const { 
      hasError, 
      error, 
      isRetrying, 
      retryCount, 
      isResetting, 
      cacheResetCount,
      lastResetResult 
    } = this.state;
    const { children, fallback, enableRetry = true, enableCacheReset = true } = this.props;

    // Show custom fallback if provided
    if (hasError && fallback) {
      return fallback;
    }

    // Show retry/reset UI if error occurred
    if (hasError && error) {
      const isCacheError = this.isCacheRelatedError(error);
      const canRetry = enableRetry && retryCount < MAX_RETRIES;
      const canResetCache = enableCacheReset && cacheResetCount < MAX_CACHE_RESETS;

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>

              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                {isCacheError ? 'Cache Error Detected' : 'Application Error'}
              </h2>

              <p className="text-sm text-gray-600 mb-4">
                {isCacheError 
                  ? 'A cache-related error occurred. This might be due to outdated data or storage issues.'
                  : 'An unexpected error occurred while loading the application.'
                }
              </p>

              {isDevelopment && (
                <details className="text-left mb-4 p-3 bg-gray-100 rounded text-xs">
                  <summary className="cursor-pointer font-medium">Error Details</summary>
                  <pre className="mt-2 whitespace-pre-wrap">{error.message}</pre>
                  {error.stack && (
                    <pre className="mt-2 whitespace-pre-wrap text-gray-500">{error.stack}</pre>
                  )}
                </details>
              )}

              {lastResetResult && (
                <div className="mb-4 p-3 bg-green-50 rounded text-sm">
                  <p className="text-green-800">
                    Cache reset completed: {lastResetResult.localStorageKeysCleared} keys cleared
                  </p>
                </div>
              )}

              <div className="space-y-3">
                {isRetrying && (
                  <div className="flex items-center justify-center space-x-2 text-blue-600">
                    <Spinner size="sm" />
                    <span className="text-sm">Retrying...</span>
                  </div>
                )}

                {isResetting && (
                  <div className="flex items-center justify-center space-x-2 text-blue-600">
                    <Spinner size="sm" />
                    <span className="text-sm">Resetting cache...</span>
                  </div>
                )}

                {!isRetrying && !isResetting && (
                  <>
                    <Button 
                      onClick={this.handleManualReset}
                      className="w-full"
                      variant="default"
                    >
                      Try Again
                    </Button>

                    {canRetry && isCacheError && (
                      <Button 
                        onClick={() => this.scheduleRetry(500)}
                        className="w-full"
                        variant="outline"
                      >
                        Auto Retry ({MAX_RETRIES - retryCount} attempts left)
                      </Button>
                    )}

                    {canResetCache && (
                      <Button 
                        onClick={this.handleCacheReset}
                        className="w-full"
                        variant="outline"
                      >
                        Reset Cache & Retry
                      </Button>
                    )}

                    {isDevelopment && (
                      <Button 
                        onClick={this.handleAtomicReset}
                        className="w-full"
                        variant="destructive"
                        size="sm"
                      >
                        🚨 Full Atomic Reset (Dev)
                      </Button>
                    )}
                  </>
                )}
              </div>

              <p className="text-xs text-gray-500 mt-4">
                {retryCount > 0 && `Retry attempts: ${retryCount}/${MAX_RETRIES} • `}
                {cacheResetCount > 0 && `Cache resets: ${cacheResetCount}/${MAX_CACHE_RESETS}`}
              </p>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}

/**
 * Functional wrapper component for easier usage
 */
interface CacheErrorBoundaryProps {
  children: ReactNode;
  queryClient?: QueryClient;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  fallback?: ReactNode;
  enableRetry?: boolean;
  enableCacheReset?: boolean;
}

export function CacheErrorBoundary(props: CacheErrorBoundaryProps): JSX.Element {
  return <CacheInvalidationErrorBoundary {...props} />;
}

export default CacheErrorBoundary;